import * as React from 'react'

import { ChevronLeft, X } from 'lucide-react'
import { observer } from 'mobx-react'

import Avatar, {
  AvatarSize,
} from '~/client/src/shared/components/Avatar/Avatar'
import AvatarInput from '~/client/src/shared/components/AvatarInput/AvatarInput'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Input } from '~/client/src/shared/components/shadcn-ui/input'
import UserListSelector from '~/client/src/shared/components/UserListSelector/UserListSelector'
import {
  IUserListSelectorStores,
  useUserListSelectorData,
} from '~/client/src/shared/components/UserListSelector/useUserListSelectorData'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'

import { Chat } from '../core/protocol/types'
import { isOk } from '../core/result'
import { ChatSession } from '../core/stores/Session'
import { WebContactDirectory } from '../web/directory'

const SEPARATOR_INSET = 42

export type GroupFormMode =
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly chat: Chat }
  | { readonly kind: 'view'; readonly chat: Chat }

type FormStep = 'members' | 'details'

interface IProps {
  session: ChatSession
  directory: WebContactDirectory
  mode: GroupFormMode
  selectorStores: IUserListSelectorStores
  onCreated: (chat: Chat) => void
  onBack: () => void
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join()
}

// One form, three modes: create walks members → details, edit starts on
// details (creator only), view is the same details read-only. The member
// picker is an internal step, not a separate route, so selection never has
// to travel through navigation params.
function GroupFormScreen({
  session,
  directory,
  mode,
  selectorStores,
  onCreated,
  onBack,
}: IProps): React.ReactElement {
  const userId = session.config.userId
  const initialChat = mode.kind === 'create' ? null : mode.chat
  const isReadonly = mode.kind === 'view'

  const [step, setStep] = React.useState<FormStep>(
    mode.kind === 'create' ? 'members' : 'details',
  )
  const [selectedIds, setSelectedIds] = React.useState<string[]>(() =>
    initialChat ? initialChat.memberIds.filter(id => id !== userId) : [],
  )
  const [name, setName] = React.useState(initialChat?.name || '')
  const [avatarUrl, setAvatarUrl] = React.useState(initialChat?.avatar || '')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string>(null)

  const { users, userMetadata, currentUser, filterData } =
    useUserListSelectorData(selectorStores)

  const trimmedName = name.trim()
  const isFormValid = !!trimmedName && selectedIds.length >= 1
  const isChanged =
    !initialChat ||
    trimmedName !== initialChat.name ||
    (avatarUrl || '') !== (initialChat.avatar || '') ||
    !sameMembers(
      [userId, ...selectedIds],
      initialChat.memberIds as readonly string[],
    )
  const canSubmit = isFormValid && isChanged && !isSubmitting

  const toggleMember = (memberId: string): void => {
    setSelectedIds(ids =>
      ids.includes(memberId)
        ? ids.filter(id => id !== memberId)
        : [...ids, memberId],
    )
  }

  const handleSubmit = async (): Promise<void> => {
    setSubmitError(null)
    setIsSubmitting(true)

    if (mode.kind === 'create') {
      const result = await session.createGroup(
        trimmedName,
        avatarUrl,
        selectedIds,
      )
      setIsSubmitting(false)
      if (isOk(result)) {
        onCreated(result.value)
      } else {
        setSubmitError(Localization.translator.couldNotCreateGroup)
      }
      return
    }

    if (mode.kind === 'edit') {
      const result = await session.updateGroup({
        ...mode.chat,
        name: trimmedName,
        avatar: avatarUrl || undefined,
        memberIds: [userId, ...selectedIds],
      })
      setIsSubmitting(false)
      if (isOk(result)) {
        onBack()
      } else {
        setSubmitError(Localization.translator.couldNotUpdateGroup)
      }
    }
  }

  const handleHeaderBack = (): void => {
    if (step === 'members' && mode.kind !== 'create') {
      setStep('details')
    } else if (step === 'details' && mode.kind === 'create') {
      setStep('members')
    } else {
      onBack()
    }
  }

  const renderScreenHeader = (title: string): React.ReactElement => (
    <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-4 tw:pb-2.5 tw:pt-3">
      <Button variant="primary-ghost" size="icon" onClick={handleHeaderBack}>
        <ChevronLeft />
      </Button>
      <span className="tw:flex-1 tw:text-center tw:text-base tw:font-semibold tw:text-foreground">
        {title}
      </span>
      <span className="tw:w-8" />
    </div>
  )

  const renderPrimaryButton = (
    label: string,
    isEnabled: boolean,
    onPress: () => void,
  ): React.ReactElement => (
    <div className="tw:px-4 tw:pb-4">
      <Button
        variant="default"
        disabled={!isEnabled}
        onClick={onPress}
        className="tw:w-full"
      >
        {label}
      </Button>
    </div>
  )

  const renderMembersStep = (): React.ReactElement => (
    <div className="tw:flex tw:h-full tw:flex-col">
      {renderScreenHeader(Localization.translator.addMembers)}
      <UserListSelector
        title=""
        selectedUserIds={new Set(selectedIds)}
        onUserToggle={toggleMember}
        users={users.filter(user => user.id !== userId)}
        userMetadata={userMetadata}
        currentUser={currentUser}
        filterData={filterData}
        selectionMode="multi"
        className="tw:min-h-0 tw:flex-1"
        autoFocusSearch={false}
      />
      {renderPrimaryButton(
        mode.kind === 'create'
          ? Localization.translator.next
          : Localization.translator.done,
        selectedIds.length >= 1,
        () => setStep('details'),
      )}
    </div>
  )

  const renderMemberRow = (
    memberId: string,
    index: number,
  ): React.ReactElement => {
    const member: User | undefined = directory.getProjectMember(memberId)
    const isSelf = memberId === userId

    return (
      <React.Fragment key={memberId}>
        {index > 0 && (
          <div
            className="tw:h-px tw:bg-border"
            style={{ marginLeft: SEPARATOR_INSET }}
          />
        )}
        <div className="tw:flex tw:items-center tw:py-2.5">
          <Avatar
            avatarType={AvatarType.User}
            src={member?.avatarUrl}
            isOnline={!isSelf && session.inbox.isOnline(memberId)}
          />
          <div className="tw:ml-2.5 tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
            <span className="tw:truncate tw:font-semibold tw:text-foreground">
              {member?.fullName || Localization.translator.unknownUser}
            </span>
            <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
              {isSelf
                ? Localization.translator.you
                : directory.directSubtitle(memberId)}
            </span>
          </div>
          {!isReadonly && !isSelf && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => toggleMember(memberId)}
              className="tw:ml-2.5"
            >
              <X />
            </Button>
          )}
        </div>
      </React.Fragment>
    )
  }

  const renderDetailsStep = (): React.ReactElement => {
    const memberIds = [userId, ...selectedIds]
    const creatorName = initialChat
      ? directory.getProjectMember(initialChat.createdBy)?.fullName
      : null

    return (
      <div className="tw:flex tw:h-full tw:flex-col">
        {renderScreenHeader(
          mode.kind === 'create'
            ? Localization.translator.newGroup
            : Localization.translator.groupDetails,
        )}
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4 tw:pb-2.5">
          <div className="tw:flex tw:justify-center tw:pb-2.5">
            {isReadonly ? (
              <Avatar
                src={avatarUrl}
                avatarType={AvatarType.Company}
                size={AvatarSize.Large}
              />
            ) : (
              <AvatarInput
                data={{ url: avatarUrl }}
                onChange={setAvatarUrl}
                avatarType={AvatarType.Company}
                size={AvatarSize.Large}
                withEditor={true}
              />
            )}
          </div>
          {isReadonly ? (
            <div className="tw:pb-1 tw:text-center tw:text-base tw:font-semibold tw:text-foreground">
              {name}
            </div>
          ) : (
            <Input
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder={Localization.translator.groupName}
              className="tw:mb-2.5"
            />
          )}
          {!!creatorName && (
            <div className="tw:pb-2.5 tw:text-center tw:text-xs tw:text-muted-foreground">
              {Localization.translator.createdByX(creatorName)}
            </div>
          )}
          <div className="tw:flex tw:items-center">
            <span className="tw:flex-1 tw:font-semibold tw:text-foreground">
              {`${Localization.translator.members} (${memberIds.length})`}
            </span>
            {!isReadonly && (
              <Button variant="link" onClick={() => setStep('members')}>
                {Localization.translator.add}
              </Button>
            )}
          </div>
          {memberIds.map(renderMemberRow)}
        </div>
        {!!submitError && (
          <span className="tw:pb-1 tw:text-center tw:text-xs tw:text-destructive">
            {submitError}
          </span>
        )}
        {!isReadonly &&
          renderPrimaryButton(
            mode.kind === 'create'
              ? Localization.translator.create
              : Localization.translator.update_verb,
            canSubmit,
            handleSubmit,
          )}
      </div>
    )
  }

  return step === 'members' ? renderMembersStep() : renderDetailsStep()
}

export default observer(GroupFormScreen)
